// withPlayerTrackingEvent.js
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import getAssetData from '../selector/getAssetData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getMessageData from '../selector/getMessageData.js';
import getOperationData from '../selector/getOperationData.js';
import { Accuracy } from '../selector/getTimestampOffset.js';
import trackMixpanel, { EventTypes, getViewId } from './mixpanel.js';
import getWidevineInfo from './getWidevineInfo.js';
import { getIsOnWebviewApp } from '../resource/getUserAgent.js';
import postMessageToSwagApp from '../action/postMessageToSwagApp.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { FEATURE_WEBVIEW_APP_SCREENSHOT } from '../RemoteConfigKeys.js';

const { PLAYER_VISIBLE, PLAYER_STARTED, PLAYER_STOPPED, PLAYER_HIDDEN } =
  EventTypes;

export const VIDEO_CATEGORY = {
  FLIX: 'flix',
  POST: 'post',
  SHORTS: 'shorts',
  STORY: 'story',
  LIVESTREAM: 'livestream',
  LIVESTREAM_DASH: 'livestream_dash',
  LIVESTREAM_BYTEPLUS: 'livestream_byteplus',
  LIVESTREAM_TENCENT: 'livestream_tencent',
};

const isOnWebviewApp = getIsOnWebviewApp();

function withPlayerTrackingEvent(WrappedComponent, category) {
  const isLiveStream = [
    VIDEO_CATEGORY.LIVESTREAM,
    VIDEO_CATEGORY.LIVESTREAM_DASH,
    VIDEO_CATEGORY.LIVESTREAM_BYTEPLUS,
    VIDEO_CATEGORY.LIVESTREAM_TENCENT,
  ].includes(category);

  class PlayerTrackingEvent extends React.Component {
    wrappedComponentRef = React.createRef();
    durationMap = new Map();

    trackPlayerEventAsVisibilityChanged = ({ isBrowserTabVisible }) => {
      const timestamp =
        Date.now() + window.__ntp__.getNtpTimestampOffset(Accuracy.MILLISECOND);
      if (isBrowserTabVisible) {
        this.trackPlayerEvent({
          type: EventTypes.PLAYER_VISIBLE,
          timestamp,
        });
      } else {
        this.trackPlayerEvent({
          type: EventTypes.PLAYER_HIDDEN,
          timestamp,
          shouldSendBeacon: true,
        });
      }
    };

    trackPlayerEvent = async ({
      type,
      timestamp,
      shouldSendBeacon = false,
      prevProps, // used by Story
      resourceUri,
    }) => {
      const {
        location,
        disableTrackPlayingTime,
        isPrivateStreamUnlocked,
        streamStatus,
        streamId,
        messageId,
        postId,
        assetId,
        assetIds,
        senderId,
        senderName,
        senderUsername,
        badges,
        mediaType,
        canViewMedia,
        isUnlocked,
        creator,
        streamName,
        appName,
        roomId,
        userId,
        isWebviewAppScreenshotEnabled,
        postMessageToSwagApp,
      } = prevProps || this.props;

      if (disableTrackPlayingTime) return;
      if (
        ![
          PLAYER_VISIBLE,
          PLAYER_STARTED,
          PLAYER_STOPPED,
          PLAYER_HIDDEN,
        ].includes(type)
      ) {
        return;
      }
      if (
        'production' !== process.env.NODE_ENV &&
        [PLAYER_STARTED, PLAYER_STOPPED].includes(type) &&
        !resourceUri
      ) {
        const player = this.wrappedComponentRef?.current?.player;
        if (!player) {
          throw new ReferenceError(`
            The "player" is not well set in wrapped component.
            If your component is class component, please set "this.player";
            if your component is function component, please leverage "useImperativeHandle" to expose player.
          `);
        }
      }

      // (2024.06)
      // 1.
      // There is an edge case for `PLAYER_VISIBLE`:
      // When the Shorts has changed from AD to regular message
      // (ShortsDetail's replace() and ShortMessage's handlePlayerSetup() are both invoked at that time),
      // neither `location.pathname` nor `this.pathname` would be correct even though the messageId is up to date.
      // At the moment, `location.pathname` would be AD's url rather than the message's url;
      // `this.pathname (window.location.pathname)` would be the url of previous message rather than the latest one.
      // 2.
      // The reason why messageId and pathname is out of sync:
      // Shorts has multiple `ShortMessage`s so that they are all equipped with correct messageId in the beginning.
      // (The Shorts can play correctly even without replacing the URL)
      // 3.
      // To make sure that it can fit all the cases for Shorts, we provide pathname based on messageId by ourselves.
      const viewId = getViewId({
        pathname:
          category === VIDEO_CATEGORY.SHORTS && messageId
            ? `/shorts/${messageId}`
            : location.pathname || this.pathname,
      });
      // Take Shorts for example,
      // the first URL would be replaced from '/shorts' to '/shorts/:messageId',
      // that's why we add this to filter that meanless tracking.
      if (!viewId) return;

      const messageStatus = canViewMedia || isUnlocked ? 'unlocked' : 'locked';
      const resource = resourceUri || this.getResource();
      const { securityLevel, hdcpStatus } = await getWidevineInfo({
        keySystem: 'com.widevine.alpha',
      });
      let extraProps = {
        'user.username': senderName || senderUsername || creator,
        'user.id': senderId || streamId,
        'media.type': isLiveStream
          ? 'video'
          : 0 === mediaType.search('video')
            ? 'video'
            : 'image',
        resource,
      };
      if (isLiveStream) {
        extraProps = {
          ...extraProps,
          'stream.status': streamStatus,
          'stream.unlocked': isPrivateStreamUnlocked,
        };
      } else {
        extraProps = {
          ...extraProps,
          'asset.id': assetId || assetIds[0],
          'message.status': messageStatus,
          'widevine.security.level': securityLevel,
          'hdcp.2.2.status': hdcpStatus,
          badges,
        };
      }

      if (category === VIDEO_CATEGORY.LIVESTREAM_BYTEPLUS) {
        extraProps['stream.roomId'] = roomId;
        extraProps['stream.userId'] = userId;
      }

      if (category === VIDEO_CATEGORY.LIVESTREAM_TENCENT) {
        extraProps['stream.streamName'] = streamName;
        extraProps['stream.appName'] = appName;
      }

      const payload = {
        viewId,
        shouldSendBeacon,
        extraProps,
      };
      if (isLiveStream) {
        payload.streamId = streamId;
      } else {
        payload.messageId = messageId || postId;
      }

      let playedDuration = 0;
      if (type === PLAYER_HIDDEN) {
        if (this.durationMap.has(PLAYER_VISIBLE)) {
          playedDuration = Math.floor(
            (timestamp - this.durationMap.get(PLAYER_VISIBLE)) / 1000
          );
          this.durationMap.delete(PLAYER_VISIBLE);
        } else {
          return;
        }
        payload.extraProps.duration = playedDuration;
      } else if (type === PLAYER_STOPPED) {
        if (this.durationMap.has(resource)) {
          playedDuration = Math.floor(
            (timestamp - this.durationMap.get(resource)) / 1000
          );
          this.durationMap.delete(resource);

          if (isOnWebviewApp && isWebviewAppScreenshotEnabled) {
            postMessageToSwagApp({
              message: 'screenshot',
              messageArgs: ['allow'],
            });
          }
        } else {
          // STORY:
          // - Happened when (messageId changed && (video ended sent || image timer over)).
          // - It would/should not enter here if users change messages via click/keydown/touch.
          return;
        }
        payload.extraProps.duration = playedDuration;
      } else if (type === PLAYER_STARTED) {
        if (this.durationMap.has(resource)) {
          // For something like bad connection. Let's just record the first one.
          return;
        } else {
          this.durationMap.set(resource, timestamp);

          if (isOnWebviewApp && isWebviewAppScreenshotEnabled) {
            postMessageToSwagApp({
              message: 'screenshot',
              messageArgs: ['deny'],
            });
          }
        }
      } else if (type === PLAYER_VISIBLE) {
        if (this.durationMap.has(PLAYER_VISIBLE)) {
          // Just in case. Shouldn't meet this condition though.
          await this.trackPlayerEvent({ type: PLAYER_HIDDEN, timestamp });
        }
        this.durationMap.set(PLAYER_VISIBLE, timestamp);
      }

      trackMixpanel({ type, payload });
    };

    getResource = () => {
      const player = this.wrappedComponentRef?.current?.player;
      if (!player) return '';
      let resource;
      if (category === VIDEO_CATEGORY.LIVESTREAM_DASH) {
        // try-catch is necessary for dashjs' getSource()
        try {
          resource = player?.getSource?.();
        } catch (e) {
          resource = '';
        }
      } else if (category === VIDEO_CATEGORY.LIVESTREAM) {
        resource = player?.currentSrc?.();
      } else {
        resource = player?.getAssetUri?.();
      }
      return resource;
    };

    componentDidMount() {
      // This is for those which don't have `location` prop, like `Feed`.
      this.pathname = window.location.pathname;
    }

    componentDidUpdate(prevProps) {
      const player = this.wrappedComponentRef?.current?.player;
      if (!player) return;

      const { isBrowserTabVisible } = this.props;
      if (isBrowserTabVisible !== prevProps.isBrowserTabVisible) {
        this.trackPlayerEventAsVisibilityChanged({ isBrowserTabVisible });
      }
    }

    render() {
      return (
        <WrappedComponent
          ref={this.wrappedComponentRef}
          {...this.props}
          trackPlayerEvent={this.trackPlayerEvent}
        />
      );
    }

    componentWillUnmount() {
      const player = this.wrappedComponentRef?.current?.player;
      if (player) {
        // In case that the player is released earlier, we pass `resourceUri` manually ASAP.
        const resourceUri = this.getResource();
        const timestamp =
          Date.now() +
          window.__ntp__.getNtpTimestampOffset(Accuracy.MILLISECOND);
        this.trackPlayerEvent({
          type: EventTypes.PLAYER_STOPPED,
          resourceUri,
          timestamp,
        });
        this.trackPlayerEvent({
          type: EventTypes.PLAYER_HIDDEN,
          resourceUri,
          timestamp,
        });
      }
    }
  }

  PlayerTrackingEvent.propTypes = {
    location: PropTypes.object,
    isBrowserTabVisible: PropTypes.bool,
    isPrivateStreamUnlocked: PropTypes.bool,
    streamStatus: PropTypes.string,
    streamId: PropTypes.string,
    messageId: PropTypes.string,
    postId: PropTypes.string, // Feed uses it
    assetId: PropTypes.string,
    assetIds: PropTypes.array, // Shorts uses it
    senderId: PropTypes.string,
    senderName: PropTypes.string,
    senderUsername: PropTypes.string, // Shorts uses it as `senderName`
    canViewMedia: PropTypes.bool,
    isUnlocked: PropTypes.bool, // Story uses it
    disableTrackPlayingTime: PropTypes.bool,
    mediaType: PropTypes.string,
    badges: PropTypes.array,
    creator: PropTypes.string,
    postMessageToSwagApp: PropTypes.func,
    isWebviewAppScreenshotEnabled: PropTypes.bool,
  };

  PlayerTrackingEvent.defaultProps = {
    location: {},
    isBrowserTabVisible: true,
    isPrivateStreamUnlocked: false,
    streamStatus: '',
    streamId: '',
    messageId: '',
    postId: '',
    assetId: '',
    assetIds: [],
    senderId: '',
    senderName: '',
    senderUsername: '',
    canViewMedia: null,
    isUnlocked: false,
    disableTrackPlayingTime: false,
    mediaType: '',
    badges: [],
    creator: '',
    postMessageToSwagApp: NULL_FUNCTION,
    isWebviewAppScreenshotEnabled: true,
  };

  const mapStateToProps = (
    state,
    { assetId, assetIds, messageId, streamId }
  ) => {
    const usingAssetId = assetId || assetIds?.[0];
    const newProps = {
      VIDEO_CATEGORY,
      mediaType: usingAssetId
        ? getAssetData(state, [usingAssetId], 'contentType')
        : getMessageData(state, messageId, 'mediaType'),
      isBrowserTabVisible: getOperationData(
        state,
        ['visibility'],
        'isBrowserTabVisible'
      ),
      isWebviewAppScreenshotEnabled:
        getRemoteConfigData(state, FEATURE_WEBVIEW_APP_SCREENSHOT) === 1,
    };
    return isLiveStream
      ? {
          ...newProps,
          streamStatus: getLiveStreamData(state, [streamId], 'status'),
          isPrivateStreamUnlocked: getOperationData(
            state,
            ['liveStream', 'paid', streamId],
            'isPrivateStreamUnlocked'
          ),
        }
      : newProps;
  };

  const mapDispatchToProps = dispatch => {
    return {
      postMessageToSwagApp: ({ message, messageArgs }) =>
        dispatch(postMessageToSwagApp({ message, messageArgs })),
    };
  };

  return connect(mapStateToProps, mapDispatchToProps)(PlayerTrackingEvent);
}

export default withPlayerTrackingEvent;
